import { Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useParams } from 'react-router-dom';

import { useClaimsQuery } from '../../api/claims/getClaims';
import { useWelcomeQuery } from '../../api/getWelcomeDetails';
import colors from '../../constants/colors';
import useAnalytics from '../../hooks/useAnalytics';
import { useChatContext } from '../../store/chat';
import { getCustomerName } from '../../utils/helpers';
import Dialog from '../Dialog';
import Button from '../styledComponents/Button';
import PhoneNumberLink from '../styledComponents/PhoneNumberLink';

export const signOutChat = async () => {
  const localStorageChatKeys = Object.keys(localStorage).filter(
    (storageItem) =>
      storageItem.startsWith('cxone') || storageItem.startsWith('_BEC')
  );

  localStorageChatKeys.forEach((key) => localStorage.removeItem(key));
};

declare global {
  interface Window {
    cxone: (...args: unknown[]) => void;
  }
}

const Chat = () => {
  const { track } = useAnalytics();
  const { data: welcomeData } = useWelcomeQuery();
  const { data: claimsData } = useClaimsQuery(!!welcomeData);
  const { firstName, lastName } = getCustomerName(welcomeData);
  const { showMaintenanceModal, setShowMaintenanceModal, chatMaintenance } =
    useChatContext();

  const location = useLocation();
  const { claimNumber, policyNumber } = useParams<{
    claimNumber: string;
    policyNumber: string;
  }>();

  const getClaimValue = () => {
    const claimsList =
      claimsData?.claims?.map((claim) => claim?.claimNumber)?.join(',') || 'NA';

    if (location?.pathname === '/claims') {
      return claimsList;
    }

    return claimNumber || 'NA';
  };

  useEffect(() => {
    if (window.cxone && welcomeData) {
      const policyList =
        welcomeData?.policies
          ?.map((policy) => policy.policyNumber)
          ?.join(',') || 'NA';

      window.cxone(
        'chat',
        'setCaseCustomField',
        'email_address',
        welcomeData?.userInfo?.email
      );
      window.cxone(
        'chat',
        'setCaseCustomField',
        'customer_id',
        welcomeData?.mdmId
      );
      window.cxone(
        'chat',
        'setCaseCustomField',
        'csaa_customer_name',
        `${firstName} ${lastName}`
      );
      window.cxone('chat', 'setCaseCustomField', 'phone_number', 'NA');
      window.cxone(
        'chat',
        'setCaseCustomField',
        'page_initiated',
        window.location.href
      );
      window.cxone(
        'chat',
        'setCaseCustomField',
        'policy_number',
        policyNumber ?? policyList
      );
      window.cxone(
        'chat',
        'setCaseCustomField',
        'claim_number',
        getClaimValue()
      );
    }
  }, [location, claimNumber, claimsData, policyNumber, welcomeData]);

  useEffect(() => {
    if (welcomeData && chatMaintenance !== undefined) {
      document?.addEventListener('click', (e) => {
        // @ts-expect-error - Event target dataset type
        // Main Chat bubble button
        if (e?.target?.dataset?.selector === 'GUIDE_CHANNEL_ICON') {
          track('View Selected', {
            event_type: 'UX Modified',
            flow: 'chat',
            selection: 'maximized',
          });

          if (chatMaintenance) {
            e.preventDefault();
            e.stopImmediatePropagation();
            setShowMaintenanceModal(true);

            window.cxone('chat', 'hideChatWindow');
            const closeChatButton = document.querySelector<HTMLButtonElement>(
              '[data-selector="GUIDE_SINGLE_MENU_CLOSE_BUTTON"]'
            );
            closeChatButton?.click();
          } else {
            // Minimize Chat Button listener
            document
              .querySelector('[data-selector=GUIDE_SINGLE_MENU_CLOSE_BUTTON]')
              ?.addEventListener('click', () => {
                track('View Selected', {
                  event_type: 'UX Modified',
                  flow: 'chat',
                  selection: 'minimized',
                });
              });

            // Header minimize button listener
            document
              .querySelector('[data-selector=HEADER_MINIMIZE_WINDOW]')
              ?.addEventListener('click', () => {
                track('View Selected', {
                  event_type: 'UX Modified',
                  flow: 'chat',
                  selection: 'minimized',
                });
              });
          }
        }
      });
    }
  }, [chatMaintenance, welcomeData]);

  return (
    <>
      {welcomeData && (
        <Helmet>
          <script>{`
        (function(n,u){
    window.CXoneDfo=n,
    window[n]=window[n]||function(){(window[n].q=window[n].q||[]).push(arguments)},window[n].u=u,
    e=document.createElement("script"),e.type="module",e.src=u+"?"+Math.round(Date.now()/1e3/3600),
    document.head.appendChild(e)
    })('cxone','https://web-modules-de-na1.niceincontact.com/loader/1/loader.js');

    cxone('init', '3797');
    cxone('guide', 'init');
    cxone('guide', 'setButtonSize', '50px');
    cxone('chat', 'enableCloseIcon', true);
    cxone('chat', 'showSendButton');
    cxone('chat', 'hideAssignedAgent');
        `}</script>
        </Helmet>
      )}
      <Dialog
        title="Chat is not available at this time"
        closeDialog={() => setShowMaintenanceModal(false)}
        open={showMaintenanceModal}
        sx={{
          zIndex: 3000,
          displayPrint: 'none',
        }}
        keepMounted
      >
        <Stack spacing={3} sx={{ my: 2 }}>
          <Stack spacing={1} sx={{ p: 3 }}>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 400,
                fontFamily: 'Cabin',
                color: colors.csaaBlack,
              }}
            >
              Chat is unavailable due to maintenance. Please check back later.
            </Typography>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 400,
                fontFamily: 'Cabin',
                color: colors.csaaBlack,
              }}
            >
              Insurance Service agents can help you at{' '}
              <PhoneNumberLink
                sx={{ fontSize: 'inherit' }}
                phoneNumber="888.980.5650"
              />
              , 24/7.
            </Typography>
          </Stack>
          <Stack
            direction="column"
            justifyContent="flex-end"
            spacing={{ mobile: 1, tablet: 2 }}
          >
            <Button
              id="chatDialogCloseButton"
              fullWidth
              variant="contained"
              sx={{
                whiteSpace: 'nowrap',
                height: { mobile: 36, tablet: 42 },
              }}
              onClick={() => setShowMaintenanceModal(false)}
              data-testid="chatDialogCloseButton"
            >
              Close
            </Button>
          </Stack>
        </Stack>
      </Dialog>
      <div className="mpc_modal_overlay" id="mpc_modal_overlay" />
    </>
  );
};

export default Chat;
